// @ts-strict-ignore
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {animate, query, stagger, style, transition, trigger} from "@angular/animations";
import {Translations} from "@translations";
import {
	SimConfigurationService,
	SimExportService,
	SimMaterialDialogService,
	NotificationService,
	SimUtilityService,
	TrackByMethods,
} from "@services";
import {ExportConfig, ExportState, ExportView, ExportVisualState, GetCurrentExportsInternalResponse, ExportType, Modules} from "@models";
import {MatTabChangeEvent} from "@angular/material/tabs";
import {EventEmitterService} from "@event-emitter";

@Component({
	selector: "sim-scenario-archive-exports-panel",
	templateUrl: "./scenario-archive-exports-panel.component.html",
	styleUrls: ["./scenario-archive-exports-panel.component.scss"],
	animations: [
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("0ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
	],
	providers: [EventEmitterService],
	standalone: false,
})
export class ScenarioArchiveExportsPanelComponent implements OnInit, OnChanges, OnDestroy {
	T = Translations;
	TrackByMethods = TrackByMethods;
	hasLicenseModule = SimConfigurationService.hasLicenseModule;

	resolutions: {displayText: string; value: string}[] = [];

	public config: ExportConfig = {
		annotations: true,
		checklists: true,
		assessments: true,
		participants: true,
		resolution: "1080p",
		exportType: ExportType.Combined,
	};

	selectedMatTabIndex: number = 0;

	@Input() scenarioRecordingId: string;
	@Input() scenarioRecordingIdList: string[] = [];
	@Input() scenarioEventId: string;
	@Input() scenarioEventIdList: string[] = [];
	@Input() assessmentIds: string[];
	@Input() reloadExportsEmitter: EventEmitter<void>;
	@Input() panelOpen: boolean;

	public exportsForScenarioRecordingWrapper: GetCurrentExportsInternalResponse;
	public loadingExports: boolean = true;
	public eventIds: string[] = [];

	private checkTimer: any = null;
	ExportState = ExportState;
	ExportVisualState = ExportVisualState;
	startingExport: boolean = false;
	exportOptionsCollapsed: boolean = true;
	public multipleExport: boolean = false;

	constructor(
		private _simExportService: SimExportService,
		private _simDialogService: SimMaterialDialogService,
		public translate: TranslateService,
		private _simNotificationService: NotificationService,
		private _eventEmitter: EventEmitterService,
	) {
		this.resolutions = [
			{
				displayText: this.translate.instant(this.T.RECORDINGS_EXPORT_DIALOG_4K),
				value: "2160p",
			},
			{
				displayText: this.translate.instant(this.T.RECORDINGS_EXPORT_DIALOG_FULL_HD),
				value: "1080p",
			},
			{
				displayText: this.translate.instant(this.T.RECORDINGS_EXPORT_DIALOG_HD_READY),
				value: "720p",
			},
		];
	}

	appliedExportOptions(exportView: ExportView) {
		return [
			{
				name: this.translate.instant(this.T.RECORDINGS_EXPORT_PANEL_WITH_ANNOTATIONS),
				value: exportView.config?.annotations,
			},
			{
				name: this.translate.instant(this.T.RECORDINGS_EXPORT_PANEL_WITH_CHECKLISTS),
				value: exportView.config?.checklists,
			},
			{
				name: this.translate.instant(this.T.RECORDINGS_EXPORT_PANEL_WITH_ASSESSMENTS),
				value: exportView.config?.assessments,
			},
			{
				name: this.translate.instant(this.T.RECORDINGS_EXPORT_PANEL_WITH_PARTICIPANTS),
				value: exportView.config?.participants,
			},
		].filter((x) => x.value);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			(this.scenarioEventId || this.scenarioRecordingId || this.scenarioRecordingIdList || this.scenarioEventIdList) &&
			!this.exportsForScenarioRecordingWrapper
		) {
			this.loadExports();
		}
		if (changes.panelOpen.currentValue !== changes.panelOpen.previousValue) {
			this.setExportStatusCheckEnabled(changes.panelOpen.currentValue);
		}
	}

	ngOnInit(): void {
		if (this.reloadExportsEmitter) {
			this.reloadExportsEmitter.subscribe(() => {
				this.loadExports();
			});
		}
	}

	ngOnDestroy() {
		this.setExportStatusCheckEnabled(false);
	}

	private setExportStatusCheckEnabled(enabled: boolean) {
		if (this.checkTimer) {
			clearInterval(this.checkTimer);
		}

		if (enabled) {
			this.checkTimer = setInterval(async () => {
				this.loadExports(false);
			}, 3000);
		}
	}

	public async loadExports(enableAutoOpenCloseSettingsCon = true) {
		let loadingExportsReq: Promise<GetCurrentExportsInternalResponse>;
		if (this.scenarioRecordingIdList && this.scenarioRecordingIdList.length > 0) this.multipleExport = true;
		else if (this.scenarioEventIdList && this.scenarioEventIdList.length > 0) this.multipleExport = true;
		else this.multipleExport = false;

		if (this.scenarioRecordingId) {
			loadingExportsReq = this._simExportService.getCurrentExports(this.scenarioRecordingId);
		} else if (this.scenarioEventId) {
			loadingExportsReq = this._simExportService.getCurrentEventExports(this.scenarioEventId);
		} else if (this.assessmentIds) {
			loadingExportsReq = this._simExportService.getCurrentAssessmentsExports(this.assessmentIds);
		} else if (this.scenarioRecordingIdList && this.scenarioRecordingIdList.length > 0) {
			loadingExportsReq = this._simExportService.getCurrentExportsByList(this.scenarioRecordingIdList);
		} else if (this.scenarioEventIdList && this.scenarioEventIdList.length > 0) {
			loadingExportsReq = this._simExportService.getCurrentEventListExports(this.scenarioEventIdList);
		}

		const task = loadingExportsReq
			.then((exportsForScenarioRecordingRes) => {
				this.exportsForScenarioRecordingWrapper = exportsForScenarioRecordingRes;
			})
			.finally(() => {
				this.loadingExports = false;
			});

		Promise.all([task, SimUtilityService.delay(1000)]).then(() => {
			this.loadingExports = false;
			if (enableAutoOpenCloseSettingsCon) {
				this.exportOptionsCollapsed = this.exportsForScenarioRecordingWrapper?.all?.length > 0;
			}
		});
	}

	public deleteExport(exportView: ExportView) {
		this._simDialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.EXPORT_DIALOG_DELETE_TITLE),
				deleteText: this.translate.instant(this.T.EXPORT_DIALOG_DELETE_DESCRIPTION),
				deleteButtonText: this.translate.instant(this.T.GLOBAL_TEXT_YES),
				cancelText: this.translate.instant(this.T.GLOBAL_TEXT_NO),
			})
			.then((data) => {
				if (data.action == "ok") {
					this._simExportService.deleteExport({exportProcessInfoId: exportView.id}).then((deleteExportResult) => {
						if (deleteExportResult) {
							this._eventEmitter.onNotifyExport.next({scenarioRecordingId: exportView.scenarioRecordingId});
							this.loadExports();
						}
					});
				}
			});
	}

	public downloadExport(exportId: string) {
		window.location.href = this._simExportService.downloadExport_Url(exportId);
	}

	public pauseExport(exportId: string) {
		this._simExportService.pause({exportProcessInfoId: exportId}).then((pauseExportResult) => {
			this.loadExports();
		});
	}

	public dismissExport(exportId: string) {
		this._simExportService.dismiss({exportProcessInfoId: exportId}).then((dismissExportResult) => {
			this.loadExports();
		});
	}

	public cancelExport(exportId: string) {
		this._simDialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.EXPORT_DIALOG_CANCEL_TITLE),
				deleteText: this.translate.instant(this.T.EXPORT_DIALOG_CANCEL_DESCRIPTION),
				deleteButtonText: this.translate.instant(this.T.GLOBAL_TEXT_YES),
				cancelText: this.translate.instant(this.T.GLOBAL_TEXT_NO),
			})
			.then((data) => {
				if (data.action == "ok") {
					this._simExportService.cancel({exportProcessInfoId: exportId}).then((pauseExportResult) => {
						this.loadExports();
					});
				}
			});
	}

	public resumeExport(exportId: string) {
		this._simExportService.resume({exportProcessInfoId: exportId}).then((pauseExportResult) => {
			this.loadExports();
		});
	}

	async startExport() {
		this.startingExport = true;
		const response = await this._simExportService
			.export(
				SimUtilityService.copy(this.config, {
					scenarioRecordingId: this.scenarioRecordingId,
					cultureCode: this.translate.currentLang,
				}),
			)
			.catch(() => {
				this.startingExport = false;
			});

		if (response) {
			if (response.isNew) {
				this.startingExport = false;
				await this.loadExports();
			} else {
				this._simNotificationService.notifyInfo(this.translate.instant(this.T.EXPORT_PROCESS_INFO_ALREADY_EXISTS));
				this.startingExport = false;
			}
		}
	}

	async startExportByList() {
		this.startingExport = true;
		try {
			const response = await this._simExportService.exportByList(
				SimUtilityService.copy(this.config, {
					scenarioRecordingIds: this.scenarioRecordingIdList,
					cultureCode: this.translate.currentLang,
				}),
			);

			if (response) {
				const isNew = response.some((res) => res.isNew);
				if (isNew) {
					this.startingExport = false;
					await this.loadExports();
				} else {
					this._simNotificationService.notifyInfo(this.translate.instant(this.T.EXPORT_PROCESS_INFO_ALREADY_EXISTS));
					this.startingExport = false;
				}
			}
		} catch (error) {
			console.error("Error exporting:", error);
			this._simNotificationService.notifyError("Error exporting");
			this.startingExport = false;
		}
	}

	public async startEventExport() {
		this.startingExport = true;
		const response = await this._simExportService
			.exportEvent(
				SimUtilityService.copy(this.config, {
					eventId: this.scenarioEventId,
					cultureCode: this.translate.currentLang,
				}),
			)
			.catch(() => {
				this.startingExport = false;
			});

		if (response && response.length > 0) {
			this.startingExport = false;
			await this.loadExports();
		} else {
			this._simNotificationService.notifyInfo(this.translate.instant(this.T.EXPORT_PROCESS_INFO_ALREADY_EXISTS));
			this.startingExport = false;
		}
	}

	public async startEventListExport() {
		this.startingExport = true;
		const response = await this._simExportService
			.exportEventsByList(
				SimUtilityService.copy(this.config, {
					eventIds: this.scenarioEventIdList,
					cultureCode: this.translate.currentLang,
				}),
			)
			.catch(() => {
				this.startingExport = false;
			});

		if (response && response.length > 0) {
			this.startingExport = false;
			await this.loadExports();
		} else {
			this._simNotificationService.notifyInfo(this.translate.instant(this.T.EXPORT_PROCESS_INFO_ALREADY_EXISTS));
			this.startingExport = false;
		}
	}
	public async startAssessmentsExport() {
		this.startingExport = true;
		const response = await this._simExportService
			.exportAssessmentsById(
				SimUtilityService.copy(this.config, {
					assessmentIds: this.assessmentIds,
					cultureCode: this.translate.currentLang,
				}),
			)
			.catch(() => {
				this.startingExport = false;
			});

		if (response && response.length > 0) {
			this.startingExport = false;
			await this.loadExports();
		} else {
			this._simNotificationService.notifyInfo(this.translate.instant(this.T.EXPORT_PROCESS_INFO_ALREADY_EXISTS));
			this.startingExport = false;
		}
	}

	matTabChanged($event: MatTabChangeEvent) {
		switch ($event.index) {
			case 0:
				this.config.exportType = ExportType.Combined;
				break;
			case 1:
				this.config.exportType = ExportType.Raw;
				break;
			case 2:
				this.config.exportType = ExportType.ExcelOnly;
				break;
		}
	}

	protected readonly ExportType = ExportType;
	protected readonly Modules = Modules;
}
